import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { ContactForm } from "@/components/LeadForms";
import { ShareButtons } from "@/components/ShareButtons";
import { api, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

export default function BlogPost() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["post", slug],
    queryFn: async () => (await api.get(`/posts/${slug}`)).data,
    retry: false,
  });
  const { data: allPosts = [] } = useQuery({ queryKey: ["all-posts"], queryFn: async () => (await api.get("/posts")).data });

  const related = useMemo(() => {
    if (!p || !allPosts.length) return [];
    const stop = new Set(["come", "senza", "della", "degli", "delle", "nella", "sono", "questo", "quali", "dopo", "tuo", "tua", "che", "gli", "per", "con", "una", "uno", "del", "dei", "alla", "sul", "più", "cosa", "casa"]);
    const words = (t) => new Set((t || "").toLowerCase().replace(/[^a-zàèéìòù0-9\s]/g, " ").split(/\s+/).filter((w) => w.length > 3 && !stop.has(w)));
    const cur = words(`${p.title} ${p.excerpt || ""}`);
    return allPosts
      .filter((x) => x.slug !== p.slug)
      .map((x) => {
        let score = 0;
        words(x.title).forEach((k) => { if (cur.has(k)) score += 1; });
        return { x, score };
      })
      .sort((a, b) => b.score - a.score || new Date(b.x.created_at) - new Date(a.x.created_at))
      .slice(0, 3)
      .map((r) => r.x);
  }, [p, allPosts]);

  useSeo({
    title: p ? `${p.meta_title || p.title} | Edilstudio` : "Articolo | Edilstudio",
    description: p ? (p.meta_description || p.excerpt) : "",
    canonicalPath: `/blog/${slug}`,
    jsonLd: p ? { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, datePublished: p.created_at, author: { "@type": "Organization", name: p.author || "Edilstudio" } } : null,
  });

  if (isLoading) return <PublicLayout><div className="py-32 text-center font-mono text-[#8695A6]">Caricamento…</div></PublicLayout>;
  if (isError || !p) return <PublicLayout><div className="py-32 text-center"><p className="text-[#1B2A41] text-xl font-semibold">Articolo non trovato</p><Link to="/blog" className="text-[#2472C8] font-semibold mt-4 inline-block">← Torna al blog</Link></div></PublicLayout>;

  const isHtml = /<[a-z][\s\S]*>/i.test(p.content || "");

  return (
    <PublicLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="flex items-center gap-2 text-[#566476] hover:text-[#1B2A41] mb-8"><ArrowLeft className="w-4 h-4" /> Torna al blog</Link>
        <div className="flex items-center gap-1.5 text-xs font-mono text-[#2472C8]"><Calendar className="w-3.5 h-3.5" /> {new Date(p.created_at).toLocaleDateString("it-IT")} · {p.author}</div>
        <h1 className="font-display text-4xl font-bold text-[#1B2A41] mt-3 leading-tight">{p.title}</h1>
        {p.excerpt && <p className="text-lg text-[#566476] mt-4 leading-relaxed">{p.excerpt}</p>}
        {p.cover_image && <img src={mediaUrl(p.cover_image)} alt={p.title} className="w-full rounded-2xl mt-8 aspect-video object-cover" />}
        <div className="mt-6"><ShareButtons slug={p.slug} title={p.title} /></div>
        <div className="prose-edil mt-8">
          {isHtml ? <div dangerouslySetInnerHTML={{ __html: p.content }} /> : <div className="whitespace-pre-line">{p.content}</div>}
        </div>

        {related.length > 0 && (
          <div className="mt-14 pt-10 border-t border-[#DCE6EF]" data-testid="blog-related">
            <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Continua a leggere</h2>
            <div className="grid sm:grid-cols-3 gap-5 mt-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group block rounded-2xl overflow-hidden border border-[#DCE6EF] bg-white hover:shadow-xl transition-shadow" data-testid={`related-${r.slug}`}>
                  {r.cover_image && <div className="aspect-video overflow-hidden"><img src={mediaUrl(r.cover_image)} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /></div>}
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-[#1B2A41] leading-snug line-clamp-3">{r.title}</h3>
                    <span className="inline-flex items-center gap-1 text-[#2472C8] text-xs font-semibold mt-3">Leggi <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 pt-10 border-t border-[#DCE6EF]" data-testid="blog-contact">
          <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Hai bisogno di una consulenza?</h2>
          <p className="text-[#566476] mt-2 leading-relaxed">Raccontaci la tua esigenza: il team Edilstudio ti risponde con una consulenza su misura, senza impegno.</p>
          <div className="bg-[#F6F9FC] rounded-2xl border border-[#DCE6EF] p-6 sm:p-8 mt-6">
            <ContactForm subject={`Articolo: ${p.title}`} whatsappMsg={`Salve, ho letto l'articolo "${p.title}" e vorrei una consulenza.`} />
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
